import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import CareNoteEntry from "../../components/employee/CareNoteEntry";
import { 
  ChevronLeft, FileText, Send, Clock, CheckCircle, 
  AlertCircle, Loader2, AlertTriangle, Info
} from "lucide-react";

export default function VisitCareNotes() {
  const { visitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [visit, setVisit] = useState(null);
  const [careNote, setCareNote] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [savedEntries, setSavedEntries] = useState({});

  useEffect(() => {
    loadData();
  }, [visitId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [visitRes, promptsRes, noteRes] = await Promise.all([
        api.get(`/employee/visits/${visitId}`),
        api.get("/care-notes/prompts"),
        api.get(`/care-notes/visit/${visitId}`),
      ]);
      
      setVisit(visitRes.data);
      setPrompts(promptsRes.data);
      
      if (noteRes.data.exists) {
        setCareNote(noteRes.data.care_note);
        // Pre-populate saved entries
        const entries = {};
        noteRes.data.care_note.guided_entries?.forEach(e => {
          entries[e.prompt_key] = e;
        });
        setSavedEntries(entries);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(err.response?.data?.detail || "Failed to load visit data");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEntry = async (entryData) => {
    setSaving(true);
    setError(null);
    
    try {
      if (careNote?.id) {
        // Update existing entry
        await api.put(`/care-notes/${careNote.id}/entry/${entryData.prompt_key}`, entryData);
      } else {
        // Create new care note with this entry
        const { data } = await api.post(`/care-notes/visit/${visitId}`, {
          visit_id: visitId,
          entries: [entryData],
        });
        setCareNote(data.care_note);
      }
      
      // Update local state
      setSavedEntries(prev => ({
        ...prev,
        [entryData.prompt_key]: {
          ...entryData,
          final_response: entryData.response,
          original_response: entryData.response,
        },
      }));
      
      // Reload care note to get updated data
      const { data: noteRes } = await api.get(`/care-notes/visit/${visitId}`);
      if (noteRes.exists) {
        setCareNote(noteRes.care_note);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!careNote?.id) {
      setError("Please complete at least one entry before submitting");
      return;
    }
    
    // Check required prompts
    const requiredPrompts = prompts.filter(p => p.required);
    const missingRequired = requiredPrompts.filter(p => {
      const entry = savedEntries[p.key];
      return !entry || (!entry.final_response && !entry.is_not_applicable);
    });
    
    if (missingRequired.length > 0) {
      setError(`Please complete required fields: ${missingRequired.map(p => p.prompt_text).join(", ")}`);
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      await api.post(`/care-notes/${careNote.id}/submit`);
      
      // Reload to get updated status
      const { data: noteRes } = await api.get(`/care-notes/visit/${visitId}`);
      if (noteRes.exists) {
        setCareNote(noteRes.care_note);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit care note");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = () => {
    if (!careNote) return null;
    
    const statusConfig = {
      draft: { color: "bg-gray-100 text-gray-700", icon: Clock, label: "Draft" },
      submitted: { color: "bg-blue-100 text-blue-700", icon: Send, label: "Submitted" },
      approved: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle, label: "Approved" },
      rejected: { color: "bg-red-100 text-red-700", icon: AlertCircle, label: "Needs Correction" },
    };
    
    const config = statusConfig[careNote.status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-3" />
          <div className="text-gray-900 font-medium">Visit not found</div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-[#06372B] font-medium"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const isEditable = !careNote || ["draft", "rejected"].includes(careNote.status);
  const clientName = visit.client ? `${visit.client.first_name} ${visit.client.last_name}` : "Client";

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-[#06372B] text-white px-4 py-5 sticky top-0 z-40">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 -ml-2 rounded-full hover:bg-white/10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="font-semibold text-lg">Care Notes</div>
            <div className="text-sm text-white/70">{clientName} - {visit.visit_date || "Today"}</div>
          </div>
          {getStatusBadge()}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Instructions Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Documentation Guidelines</p>
              <ul className="space-y-1 text-blue-700">
                <li>Use voice recording for quick entries</li>
                <li>AI Polish improves grammar only - never adds facts</li>
                <li>Mark sections as &quot;Not applicable&quot; if they don&apos;t apply</li>
                <li>Flag urgent concerns for immediate office attention</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Rejection reason if applicable */}
        {careNote?.status === "rejected" && careNote.correction_history?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-red-800">Corrections Needed</div>
                <div className="text-sm text-red-700 mt-1">
                  {careNote.correction_history[careNote.correction_history.length - 1]?.reason}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          </div>
        )}

        {/* Care Note Entries */}
        <div className="space-y-4">
          {prompts.map((prompt) => (
            <CareNoteEntry
              key={prompt.key}
              prompt={prompt}
              entry={savedEntries[prompt.key]}
              onSave={handleSaveEntry}
              isLoading={saving}
              disabled={!isEditable}
            />
          ))}
        </div>

        {/* Submit Section */}
        {isEditable && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">Ready to submit?</div>
                <div className="text-sm text-gray-500">
                  Submit your care notes for supervisor review
                </div>
              </div>
              <button
                onClick={handleSubmitForReview}
                disabled={submitting || !careNote?.id}
                className="flex items-center gap-2 py-3 px-6 rounded-xl bg-[#06372B] text-white font-medium hover:bg-[#0a4a3a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-care-notes-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Submit for Review
              </button>
            </div>
          </div>
        )}

        {/* Submitted/Approved status message */}
        {careNote?.status === "submitted" && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
            <Send className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <div className="font-medium text-blue-900">Submitted for Review</div>
            <div className="text-sm text-blue-700 mt-1">
              Your supervisor will review these care notes.
            </div>
          </div>
        )}

        {careNote?.status === "approved" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
            <div className="font-medium text-emerald-900">Approved</div>
            <div className="text-sm text-emerald-700 mt-1">
              Approved by {careNote.approved_by_name || "Supervisor"} on{" "}
              {new Date(careNote.approved_at).toLocaleDateString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
